import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Route,
  Clock,
  Loader2,
  X,
  CheckCircle2,
  GripVertical,
  RotateCcw,
  Trash2,
  ExternalLink,
  Printer,
  MapPin,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DEPOT_LOCATION } from "@/config/locations";
import { openPrintableRouteList } from "@/components/loads/PrintableRouteList";
import { toast } from "sonner";
import type { InstallationMarker, OptimizeResult } from "@/hooks/useLoadCreationMap";

// Status and task type filter options
const statusOptions = [
  { value: "ready_for_installation", label: "Valmis asennukseen" },
  { value: "confirmed", label: "Vahvistettu" },
  { value: "all_active", label: "Kaikki aktiiviset" },
];

const taskTypeOptions = [
  { value: "all", label: "Kaikki tehtävät" },
  { value: "new_installation", label: "Asennukset (uudet)" },
  { value: "return", label: "Palautukset (pajalta)" },
  { value: "pickup", label: "Noudot (pajalle)" },
];

interface Installer {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

export interface LoadCreationSidebarProps {
  // Form data
  loadDate: string;
  setLoadDate: (date: string) => void;
  installerId: string;
  setInstallerId: (id: string) => void;
  notes: string;
  setNotes: (notes: string) => void;

  // Filters
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  taskTypeFilter: string;
  setTaskTypeFilter: (taskType: string) => void;

  // Selection
  selectedIds: Set<string>;
  markers: InstallationMarker[];
  selectedMarkers: InstallationMarker[];

  // Optimization
  optimizeResult: OptimizeResult | null;
  setOptimizeResult: (result: OptimizeResult | null) => void;

  // Data
  installers: Installer[] | undefined;

  // Actions
  onOptimize: () => void;
  onCreateLoad: () => void;
  onClearSelection: () => void;
  onRemoveItem: (id: string) => void;

  // Loading states
  isOptimizing: boolean;
  isCreating: boolean;
}

// Sortable item component for drag and drop
interface SortableItemProps {
  id: string;
  marker: InstallationMarker;
  index: number;
  isOptimized: boolean;
  onRemove: () => void;
}

function SortableItem({ id, marker, index, isOptimized, onRemove }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded-md bg-muted/50"
    >
      {isOptimized && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      {isOptimized ? (
        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
          {index + 1}
        </div>
      ) : (
        <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">
          {marker.orderNumber}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {marker.cemeteryName}, {marker.cemeteryCity}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        onClick={onRemove}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function LoadCreationSidebar({
  loadDate,
  setLoadDate,
  installerId,
  setInstallerId,
  notes,
  setNotes,
  statusFilter,
  setStatusFilter,
  taskTypeFilter,
  setTaskTypeFilter,
  selectedIds,
  markers,
  selectedMarkers,
  optimizeResult,
  setOptimizeResult,
  installers,
  onOptimize,
  onCreateLoad,
  onClearSelection,
  onRemoveItem,
  isOptimizing,
  isCreating,
}: LoadCreationSidebarProps) {
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && optimizeResult) {
      const oldIndex = optimizeResult.optimizedOrder.findIndex(
        (wp) => wp.waypointId === active.id
      );
      const newIndex = optimizeResult.optimizedOrder.findIndex(
        (wp) => wp.waypointId === over.id
      );

      const newOrder = arrayMove(optimizeResult.optimizedOrder, oldIndex, newIndex).map(
        (wp, index) => ({
          ...wp,
          position: index + 1,
        })
      );

      setOptimizeResult({
        ...optimizeResult,
        optimizedOrder: newOrder,
      });

      toast.info("Järjestys päivitetty manuaalisesti");
    }
  };

  return (
    <div className="space-y-4 overflow-y-auto">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Kuorman tiedot</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Tilauksen tila</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tehtävätyyppi</Label>
              <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {taskTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date">Päivämäärä</Label>
            <Input
              id="date"
              type="date"
              value={loadDate}
              onChange={(e) => setLoadDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="installer">Asentaja</Label>
            <Select value={installerId} onValueChange={setInstallerId}>
              <SelectTrigger
                className={
                  selectedIds.size > 0 && !installerId
                    ? "border-destructive ring-destructive"
                    : ""
                }
              >
                <SelectValue placeholder="Valitse asentaja" />
              </SelectTrigger>
              <SelectContent>
                {installers?.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.first_name} {i.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Muistiinpanot</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Lisätietoja kuormasta..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Valitut kohteet ({selectedIds.size})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedIds.size === 0 ? (
            <p className="text-sm text-muted-foreground">
              Klikkaa kartan merkintöjä valitaksesi kohteita kuormaan.
            </p>
          ) : optimizeResult ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={optimizeResult.optimizedOrder.map((wp) => wp.waypointId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {optimizeResult.optimizedOrder.map((wp, index) => {
                    const marker = markers.find((m) => m.id === wp.waypointId);
                    if (!marker) return null;
                    return (
                      <SortableItem
                        key={wp.waypointId}
                        id={wp.waypointId}
                        marker={marker}
                        index={index}
                        isOptimized={true}
                        onRemove={() => onRemoveItem(marker.id)}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="space-y-2">
              {selectedMarkers.map((marker, index) => (
                <SortableItem
                  key={marker.id}
                  id={marker.id}
                  marker={marker}
                  index={index}
                  isOptimized={false}
                  onRemove={() => onRemoveItem(marker.id)}
                />
              ))}
            </div>
          )}

          {optimizeResult && (
            <div className="mt-4 p-3 rounded-md bg-blue-50 dark:bg-blue-950 space-y-3">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <Route className="h-4 w-4" />
                  {optimizeResult.totalDistanceKm} km
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {Math.floor(optimizeResult.estimatedDurationMin / 60)}h{" "}
                  {optimizeResult.estimatedDurationMin % 60}min
                </span>
              </div>
              <div className="flex items-center gap-4">
                <a
                  href={`https://www.google.com/maps/dir/${DEPOT_LOCATION.lat},${DEPOT_LOCATION.lng}/${optimizeResult.optimizedOrder.map((wp) => `${wp.lat},${wp.lng}`).join("/")}/${DEPOT_LOCATION.lat},${DEPOT_LOCATION.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  <ExternalLink className="h-4 w-4" />
                  Google Maps
                </a>
                <button
                  onClick={() => {
                    const selectedInstaller = installers?.find(
                      (i) => i.id === installerId
                    );
                    const installerName = selectedInstaller
                      ? `${selectedInstaller.first_name} ${selectedInstaller.last_name}`.trim()
                      : "Ei valittu";

                    const routeItems = optimizeResult.optimizedOrder.map(
                      (wp, index) => {
                        const marker = markers.find(
                          (m) => m.id === wp.waypointId
                        );
                        return {
                          position: index + 1,
                          orderNumber: marker?.orderNumber || "",
                          customerName: marker?.customerName || "",
                          cemeteryName: marker?.cemeteryName || "",
                          cemeteryCity: marker?.cemeteryCity || "",
                          taskType: marker?.taskType || "",
                          lat: wp.lat,
                          lng: wp.lng,
                        };
                      }
                    );

                    openPrintableRouteList({
                      loadDate,
                      installerName,
                      notes,
                      routeItems,
                      totalDistanceKm: optimizeResult.totalDistanceKm,
                      estimatedDurationMin: optimizeResult.estimatedDurationMin,
                      depotName: DEPOT_LOCATION.name,
                    });
                  }}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  <Printer className="h-4 w-4" />
                  Tulosta
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {selectedIds.size > 0 && (
              <Button
                className="w-full gap-2"
                variant="outline"
                onClick={onClearSelection}
              >
                <Trash2 className="h-4 w-4" />
                Tyhjennä valinnat
              </Button>
            )}

            <Button
              className="w-full gap-2"
              variant="outline"
              onClick={onOptimize}
              disabled={selectedIds.size < 1 || isOptimizing}
            >
              {isOptimizing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : optimizeResult ? (
                <RotateCcw className="h-4 w-4" />
              ) : (
                <Route className="h-4 w-4" />
              )}
              {optimizeResult
                ? "Optimoi uudelleen"
                : selectedIds.size === 1
                  ? "Laske reitti"
                  : "Laske reitti ja optimoi"}
            </Button>

            {/* Validation message */}
            {(selectedIds.size === 0 || !installerId) && (
              <p className="text-sm text-destructive">
                {selectedIds.size === 0 && !installerId
                  ? "Valitse kohteita kartalta ja asentaja"
                  : selectedIds.size === 0
                    ? "Valitse vähintään yksi kohde kartalta"
                    : "Valitse asentaja pudotusvalikosta"}
              </p>
            )}

            <Button
              className="w-full"
              onClick={onCreateLoad}
              disabled={selectedIds.size === 0 || !installerId || isCreating}
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              Luo kuorma
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
